<main *ngIf="settingsInfo$ | async as settingsInfo" class="settings-container d-flex flex-column justify-content-center align-items-center">
  <div class="kh-heading-sm pt-kh-7">{{ 'SETTINGS_VIEW.GENERAL_SETTINGS' | translate }}</div>

  <div class="settings-section-container">
    <!-- Display name -->
    <kh-settings-value
      [label]="'SETTINGS_VIEW.DISPLAY_NAME' | translate"
      [value]="displayNameForm.controls.firstName.value + ' ' + displayNameForm.controls.lastName.value"
      (clickSave)="saveDisplayName()"
      (resetForm)="resetDisplayName()"
      (startEditing)="updateActiveForm(listOfControls.displayName)"
      [instanceName]="listOfControls.displayName"
      testId="display-name-value"
      data-testId="display-name-settings">
      <div formSectionDesktop *ngTemplateOutlet="displayNameFormTemplate"></div>
      <div formSectionMobile *ngTemplateOutlet="displayNameFormTemplate"></div>
    </kh-settings-value>

    <kh-divider></kh-divider>

    <!-- Password -->
    <ng-container *ngIf="!isRosteredUser">
      <kh-settings-value
        [label]="'SETTINGS_VIEW.PASSWORD' | translate"
        value="******"
        (clickSave)="updatePassword()"
        (resetForm)="resetPasswordForm()"
        (startEditing)="updateActiveForm(listOfControls.password, true)"
        [instanceName]="listOfControls.password"
        data-testid="password-settings">
        <div formSectionDesktop *ngTemplateOutlet="passwordFormTemplate"></div>
        <div formSectionMobile *ngTemplateOutlet="passwordFormTemplate"></div>
      </kh-settings-value>

      <kh-divider></kh-divider>
    </ng-container>

    <!-- Optional Linked Account -->
    <kh-settings-value
      *ngIf="getLinkedAccounts()"
      [label]="'SETTINGS_VIEW.LINKED_ACCOUNT' | translate"
      [value]="getLinkedAccounts()"
      [isEditable]="false"
      data-testid="linked-account-field">
    </kh-settings-value>

    <kh-divider></kh-divider>

    <!-- Select Language -->
    <kh-settings-value
      *ngIf="locales$ | async as locales"
      [label]="'SETTINGS_VIEW.ACCOUNT_LANGUAGE' | translate"
      [value]="getCurrentLanguage(locales)"
      class="dropdown-parent"
      (startEditing)="updateActiveForm(listOfControls.language)"
      [instanceName]="listOfControls.language"
      [hideSaveButton]="true"
      data-testid="language-settings">
      <div formSectionDesktop *ngTemplateOutlet="languageFormTemplate"></div>
      <div formSectionMobile *ngTemplateOutlet="languageFormTemplate"></div>
    </kh-settings-value>

    <kh-divider></kh-divider>

    <!-- Email -->
    <kh-settings-value
      [label]="'SETTINGS_VIEW.EMAIL' | translate"
      [value]="emailForm.controls.email.value"
      (clickSave)="saveEmail()"
      (resetForm)="resetEmail()"
      (startEditing)="updateActiveForm(listOfControls.email)"
      [instanceName]="listOfControls.email"
      [isEditable]="!isRosteredUser"
      data-testid="email-settings"
      testId="email-value">
      <div formSectionDesktop *ngTemplateOutlet="emailFormTemplate"></div>
      <div formSectionMobile *ngTemplateOutlet="emailFormTemplate"></div>
      <div customContent *ngTemplateOutlet="emailVerificationTemplate"></div>
    </kh-settings-value>

    <kh-divider></kh-divider>

    <!-- Email Notifications -->
    <kh-settings-value
      *ngIf="settingsInfo.TeacherInfo?.EmailGroups || settingsInfo.ParentInfo?.EmailGroups"
      [label]="'SETTINGS_VIEW.NOTIFICATIONS' | translate"
      value=""
      (startEditing)="showEmailNotificationsModal()"
      [instanceName]="listOfControls.emailNotifications">
    </kh-settings-value>
  </div>

  <!-- Teacher exclusive -->
  <ng-container *ngIf="settingsInfo.TeacherInfo && !hideTeacherSection">
    <div class="kh-heading-sm section-top-padding">{{ 'SETTINGS_VIEW.TEACHER_SETTINGS' | translate }}</div>

    <div class="settings-section-container">
      <kh-settings-link
        [label]="'SETTINGS_VIEW.MY_SCHOOL' | translate"
        [value]="settingsInfo.TeacherInfo.SchoolName"
        [linkLabel]="'SETTINGS_VIEW.UPDATE' | translate"
        [showLinkLabel]="!isRosteredUser"
        (actionClicked)="goToMySchool()"
        data-testid="my-school-settings">
      </kh-settings-link>

      <kh-divider></kh-divider>

      <kh-settings-link
        *ngIf="!hasActivePremiumSubscriptions()"
        [label]="'SETTINGS_VIEW.CLASS_SUBSCRIPTIONS' | translate"
        [value]="classSubscriptionsLabel()"
        [linkLabel]="'SETTINGS_VIEW.MANAGE' | translate"
        (actionClicked)="goToSubscribe()">
      </kh-settings-link>

      <kh-settings-menu
        *ngIf="hasActivePremiumSubscriptions()"
        [label]="'SETTINGS_VIEW.CLASS_SUBSCRIPTIONS' | translate"
        [value]="classSubscriptionsLabel()"
        [menuLabel]="'SETTINGS_VIEW.MANAGE' | translate">
        <ng-container menu-items>
          <kh-dropdown-menu-item (click)="goToSubscribe()">
            <div>{{ 'SETTINGS_VIEW.ADD_CLASS' | translate }}</div>
          </kh-dropdown-menu-item>
          <kh-dropdown-menu-item (click)="showCancelSubscriptionModal()">
            <div>{{ 'SETTINGS_VIEW.CANCEL_SUBSCRIPTION' | translate }}</div>
          </kh-dropdown-menu-item>
        </ng-container>
      </kh-settings-menu>
    </div>
  </ng-container>

  <!-- Parent exclusive -->
  <div *ngIf="settingsInfo.ParentInfo && hasChildWithPremium(settingsInfo.ParentInfo)" class="w-100 section-top-padding">
    <div class="kh-heading-sm w-100 text-start">{{ 'SETTINGS_VIEW.PARENTGUARDIAN_SETTINGS' | translate }}</div>
    <div class="settings-section-container">
      <kh-settings-link
        [label]="'SETTINGS_VIEW.CHILD_SETTINGS' | translate"
        [value]="getChildrenLabel(settingsInfo.ParentInfo)"
        [linkLabel]="'SETTINGS_VIEW.MANAGE' | translate"
        (actionClicked)="goToParentalControls()">
      </kh-settings-link>
    </div>
  </div>

  <!-- Role Settings -->
  <div *ngIf="settingsInfo.UserRoles.length > 1" class="w-100 section-top-padding pl-kh-2">
    <div class="kh-heading-sm text-start pb-kh-2">{{ 'SETTINGS_VIEW.ROLE_SETTINGS' | translate }}</div>
    <div class="kh-medium-title-text kh-grey-warm-dark pb-kh-4">
      {{ 'SETTINGS_VIEW.SELECT_A_ROLE_TO_SWITCH' | translate }}
    </div>

    <div class="role-selector-container">
      <kh-common-dropdown
        class="d-block mb-kh-2"
        arrowColor="kh-grey-accessible"
        [items]="settingsInfo.UserRoles"
        [placeholder]="'SETTINGS_VIEW.SELECT_ROLE' | translate"
        [(ngModel)]="selectedRole"
        (ngModelChange)="goToRole($event)"
        [getName]="getRoleLabel"
        [getValue]="getRoleValue"
        testId="role-selector-dropdown">
      </kh-common-dropdown>
    </div>
  </div>

  <!-- Delete account -->
  <div class="delete-account-container">
    <label
      class="kh-body-md"
      [translateWithFormat]="'SETTINGS_VIEW.HELP_CONTACT_PARTS.KH_MESSAGE_FORMAT'"
      [templateParams]="{
        helpCenterLink: helpCenterLink,
        contactUsLink: contactUsLink
      }">
    </label>

    <div class="d-flex mb-kh-7 mt-kh-7 desktop-only">
      <div
        (click)="goToMergeAccount()"
        class="mr-kh-5 pointer text-link link-lg text-link-secondary"
        data-testid="merge-account">
        {{ 'SETTINGS_VIEW.MERGE_ACCOUNT' | translate }}
      </div>
      <div
        (click)="displayDeleteAccountModal()"
        class="pointer text-link link-lg text-link-secondary"
        data-testid="delete-account">
        {{ 'SETTINGS_VIEW.DELETE_ACCOUNT' | translate }}
      </div>
    </div>
  </div>

  <div class="info-links">
    <a
      class="text-link link-sm text-link-secondary"
      [href]="'SETTINGS_VIEW.PRIVACY_CENTER_URL' | translate"
      target="_blank"
      rel="noopener noreferrer">
      {{ 'SETTINGS_VIEW.PRIVACY_CENTRE' | translate }}
    </a>
    <a
      class="text-link link-sm text-link-secondary"
      [href]="'SETTINGS_VIEW.PRIVACY_POLICY_URL' | translate"
      target="_blank"
      rel="noopener noreferrer">
      {{ 'SETTINGS_VIEW.PRIVACY_POLICY' | translate }}
    </a>
    <a
      class="text-link link-sm text-link-secondary"
      [href]="'SETTINGS_VIEW.TERMS_URL' | translate"
      target="_blank"
      rel="noopener noreferrer">
      {{ 'SETTINGS_VIEW.TERMS_OF_SERVICE' | translate }}
    </a>
  </div>

  <div class="mobile-only">
    <kh-divider></kh-divider>
    <div class="pointer mobile-delete-container" (click)="goToMergeAccount()">
      {{ 'SETTINGS_VIEW.MERGE_ACCOUNT' | translate }}
    </div>
    <kh-divider></kh-divider>
    <div class="pointer mobile-delete-container" (click)="displayDeleteAccountModal()">
      {{ 'SETTINGS_VIEW.DELETE_ACCOUNT' | translate }}
    </div>
  </div>
</main>

<ng-template #displayNameFormTemplate>
  <form [formGroup]="displayNameForm" novalidate data-test-id="display-name-form">
    <div class="d-flex flex-row">
      <kh-common-input
        class="mr-kh-1 flex-1"
        formControlName="firstName"
        [placeholder]="'SETTINGS_VIEW.FIRST_NAME' | translate"
        testId="edit-first-name-field">
      </kh-common-input>
      <kh-common-input
        formControlName="lastName"
        [placeholder]="'SETTINGS_VIEW.LAST_NAME' | translate"
        class="flex-1"
        testId="edit-last-name-field">
      </kh-common-input>
    </div>
  </form>
</ng-template>

<ng-template #passwordFormTemplate>
  <form [formGroup]="passwordForm" class="enter-password-container" data-test-id="change-password-container">
    <div class="kh-medium-title-text">
      {{ 'SETTINGS_VIEW.CURRENT_PASSWORD' | translate }}
    </div>
    <kh-common-input
      [formControlName]="passwordFormFields.currentPassword"
      [placeholder]="'SETTINGS_VIEW.CURRENT_PASSWORD' | translate"
      [controlType]="inputTypes.Password"
      class="mb-kh-3 d-block"
      [validationMessages]="passwordValidationMessages"
      testId="current-password-field">
    </kh-common-input>

    <div class="kh-medium-title-text">{{ 'SETTINGS_VIEW.NEW_PASSWORD' | translate }}</div>
    <kh-common-input
      [formControlName]="passwordFormFields.newPassword"
      [placeholder]="'SETTINGS_VIEW.NEW_PASSWORD' | translate"
      [controlType]="inputTypes.Password"
      class="mb-kh-2 d-block"
      [validationMessages]="passwordValidationMessages"
      testId="new-password-field">
    </kh-common-input>

    <kh-common-input
      [formControlName]="passwordFormFields.newPasswordConfirm"
      [placeholder]="'SETTINGS_VIEW.CONFIRM_NEW_PASSWORD' | translate"
      [controlType]="inputTypes.Password"
      [validationMessages]="passwordValidationMessages"
      testId="new-password-confirmation-field">
    </kh-common-input>
  </form>
</ng-template>

<ng-template #languageFormTemplate>
  <form [formGroup]="languageForm" *ngIf="locales$ | async as locales">
    <kh-common-dropdown
      class="d-block mb-kh-2 mt-2"
      [formControlName]="languageFormFields.locale"
      [items]="locales"
      [getName]="getLocaleName"
      [getValue]="getLocaleValue"
      arrowColor="kh-grey-accessible"
      [placeholder]="getLanguagePlaceholder(locales)!"
      testId="select-language-dropdown">
    </kh-common-dropdown>
  </form>
</ng-template>

<ng-template #emailFormTemplate>
  <form [formGroup]="emailForm" novalidate>
    <div class="d-flex flex-row">
      <kh-common-input
        class="mr-kh-1 email-input"
        formControlName="email"
        [placeholder]="'SETTINGS_VIEW.EMAIL' | translate"
        [validationMessages]="emailValidationMessages"
        testId="edit-email-field">
      </kh-common-input>
    </div>
  </form>
</ng-template>

<ng-template #emailVerificationTemplate>
  <div class="mr-kh-2" *ngIf="!isEmailVerified && !isRosteredUser">
    <div class="kh-small-title-text">{{ 'SETTINGS_VIEW.UNVERIFIED' | translate }}</div>
    <div role="button" class="verification-link" (click)="sendEmailVerification()">
      <span *ngIf="!verificationEmailSent">{{ 'SETTINGS_VIEW.SEND_VERIFICATION' | translate }}</span>
      <span *ngIf="verificationEmailSent">{{ 'SETTINGS_VIEW.RESEND_VERIFICATION' | translate }}</span>
    </div>
  </div>
</ng-template>

<ng-template #helpCenterLink>
  <a href="./teachers/helpcenter" class="text-link link-lg text-link-primary">
    {{ 'SETTINGS_VIEW.HELP_CONTACT_PARTS.HELP_CENTRE' | translate }}
  </a>
</ng-template>

<ng-template #contactUsLink>
  <a
    [href]="'SETTINGS_VIEW.CONTACT_US_URL' | translate"
    class="text-link link-lg text-link-primary"
    target="_blank"
    rel="noopener noreferrer">
    {{ 'SETTINGS_VIEW.HELP_CONTACT_PARTS.CONTACT_US' | translate }}
  </a>
</ng-template>